import firestore from "src/apiService/firestore";
import { getJSONFileApi } from "src/apiService/modules/uploads";
import { depopProcessFile } from "src/apiService/modules/depop";
import { vintedProcessFile } from "src/apiService/modules/vinted";
import { getUserId } from "src/config/storage";
import { getInventory } from "src/store/inventory/selector";
import { uploadFile } from "src/utils/storage";
import parallelize from "src/utils/parallelize";
import type { SyncLock } from "./syncLock";

/** Default parallel Storage reads when building the pending-Downloads skip set. */
export const PendingInventoryDownloadReadConcurrency = 5;

/**
 * Listing ids from a daily inventory Storage payload (raw platform rows, or
 * already-transformed rows with "Platform Listing Id").
 */
export function listingIdsFromInventoryDownloadPayload(
  data: unknown,
  getListingId: (item: unknown) => string | null
): string[] {
  if (!Array.isArray(data)) return [];
  const ids: string[] = [];
  for (const item of data) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;
    const id =
      getListingId(item) ||
      (record["Platform Listing Id"] != null
        ? String(record["Platform Listing Id"])
        : null) ||
      (record.listing_id != null ? String(record.listing_id) : null);
    if (id) ids.push(id);
  }
  return ids;
}

export function getExistingInventoryListingIds(
  state: unknown,
  importPlatform: string
): Set<string> {
  return new Set(
    getInventory(state as Parameters<typeof getInventory>[0])
      .filter(
        (item) =>
          item.import_platform === importPlatform && item.listing_id != null
      )
      .map((item) => String(item.listing_id))
  );
}

export type InventoryDailyDownloadType = "vintedInventory" | "depopInventory";

/**
 * A row the client could not enrich because its item page was gone. Used to
 * decide whether the Vinted processor has anything to report for this batch.
 */
function hasInventoryPageFailure(item: unknown): boolean {
  return Boolean(
    item &&
      typeof item === "object" &&
      (item as { pageFailure?: unknown }).pageFailure
  );
}

/**
 * Listing ids already queued for review after the last inventory sync cursor
 * (aborted catch-up progress). Flag off: unread Downloads. Flag on: Pending_Inventory.
 */
export async function loadPendingInventoryListingIds(args: {
  integrationId: string;
  lastSyncDate: Date;
  downloadType: InventoryDailyDownloadType;
  listingIdsFromPayload: (data: unknown) => string[];
  logLabel: string;
  concurrency?: number;
  pendingInventoryEnabled?: boolean;
  importPlatform?: string;
}): Promise<Set<string>> {
  const uid = await getUserId();
  const lastMs = args.lastSyncDate.valueOf();

  if (args.pendingInventoryEnabled) {
    const found = new Set<string>();
    // Only rows written by the unfinished run matter: everything at or before
    // the last completed sync cursor is already accounted for. Never load the
    // whole pending queue.
    let query = firestore()
      .collection("Pending_Inventory")
      .where("user", "==", uid)
      .where("timestamp", ">", lastMs);
    if (args.importPlatform) {
      query = query.where("import_platform", "==", args.importPlatform);
    }
    const snap = await query.get();
    for (const doc of snap.docs) {
      const data = doc.data();
      // Rows without an integrationId apply to every integration.
      if (
        args.integrationId &&
        data.integrationId != null &&
        String(data.integrationId) !== String(args.integrationId)
      ) {
        continue;
      }
      if (data.listing_id != null) found.add(String(data.listing_id));
    }
    return found;
  }

  const snap = await firestore()
    .collection("Downloads")
    .where("user", "==", uid)
    .where("type", "==", args.downloadType)
    .where("new", "==", true)
    .get();

  const filenames: string[] = [];
  for (const doc of snap.docs) {
    const data = doc.data();
    if (String(data.integrationId || "") !== String(args.integrationId)) {
      continue;
    }
    const ts = Number(data.timestamp);
    if (!Number.isFinite(ts) || ts <= lastMs) continue;
    if (typeof data.filename === "string" && data.filename) {
      filenames.push(data.filename);
    }
  }

  const found = new Set<string>();
  await parallelize(
    filenames,
    async (filename) => {
      try {
        const payload = await getJSONFileApi(filename);
        for (const id of args.listingIdsFromPayload(payload)) {
          found.add(id);
        }
      } catch (e) {
        console.warn(
          `[${args.logLabel}] Failed to read pending inventory download`,
          {
            filename,
            error: (e as Error).message,
          }
        );
      }
    },
    args.concurrency ?? PendingInventoryDownloadReadConcurrency
  );
  return found;
}

/** One Storage blob per list page; Downloads (flag off) or backend pending upsert (flag on). */
export async function writeInventoryDownloadBatch(args: {
  integrationId: string;
  items: unknown[];
  date: Date;
  timestamp: number;
  batchIndex: number;
  lock: SyncLock;
  /** Storage folder under `{uid}/`, e.g. `vintedInventory`. */
  storageFolder: InventoryDailyDownloadType;
  downloadType: InventoryDailyDownloadType;
  pendingInventoryEnabled?: boolean;
}): Promise<void> {
  const {
    integrationId,
    items,
    date,
    timestamp,
    batchIndex,
    lock,
    storageFolder,
    downloadType,
    pendingInventoryEnabled,
  } = args;
  if (!items.length) return;

  lock.throwIfAborted();

  const uid = await getUserId();
  const basename = `${timestamp}_${integrationId}_${String(batchIndex).padStart(
    2,
    "0"
  )}.json`;
  const filename = `${uid}/${storageFolder}/${basename}`;

  await uploadFile(filename, JSON.stringify(items));

  lock.throwIfAborted();

  // With pendingInventory on, the backend owns the batch (it upserts
  // Pending_Inventory). With the flag off the call only reports rows whose
  // page could not be fetched, so skip it unless this batch has one — the
  // Downloads doc below still belongs to the client.
  if (downloadType === "vintedInventory") {
    if (pendingInventoryEnabled) {
      await vintedProcessFile(
        integrationId,
        "inventory",
        "daily-sync",
        basename
      );
    } else if (items.some(hasInventoryPageFailure)) {
      try {
        await vintedProcessFile(
          integrationId,
          "inventory",
          "daily-sync",
          basename
        );
      } catch (e) {
        // Reporting only: never block the daily inventory download.
        console.warn("[Vinted] Inventory page-failure report failed", e);
      }
    }
  } else if (pendingInventoryEnabled) {
    await depopProcessFile(integrationId, "inventory", "daily-sync", basename);
  }

  if (pendingInventoryEnabled) return;

  const doc = firestore().collection("Downloads").doc();
  await doc.set({
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear(),
    filename,
    new: true,
    timestamp,
    type: downloadType,
    user: uid,
    integrationId,
  });
}
